import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, catchError, throwError, first, tap } from 'rxjs';
import { environment } from '@env/environment';
import { MtCaptchaApiService } from './api/mtcaptcha-api.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class MtcaptchaService {
  private hiddenValuesSubject: { [key: string]: BehaviorSubject<string> } = {};
  private privateKey = environment.captchaPublicKey;

  constructor(
    private captchaApi: MtCaptchaApiService,
    public translate: TranslateService
  ) {}

  public loadMTCaptchaScript(translationTexts: any): void {
    const mtcaptchaConfig = {
      sitekey: this.privateKey,
      autoFormValidate: true,
      widgetSize: 'mini',
      action: 'imageonly',
      customStyle: {
        cardShadowColor: '',
        cardBorder: '1px solid #888888',
        buttonIconColor: {
          refresh: '#757575',
          audio: '#757575',
        },
      },
      lang: 'de',
      customLangText: {
        de: {
          inputPrompt: translationTexts.inputPrompt,
          reload: translationTexts.reload,
          verifying: translationTexts.verifying,
          verifyFail: translationTexts.verifyFail,
          verifySuccess: translationTexts.verifySuccess,
          captchaExpired: translationTexts.captchaExpired,
          verifyExpired: translationTexts.verifyExpired,
          emptyCaptcha: translationTexts.emptyCaptcha,
          incompleteCaptcha: translationTexts.incompleteCaptcha,
          audioPlay: translationTexts.audioPlay,
          audioPlaying: translationTexts.audioPlaying,
          continueAudio: translationTexts.continueAudio,
          downloadAudio: translationTexts.downloadAudio,
          audioDownloading: translationTexts.audioDownloading,
          connectionError: translationTexts.connectionError,
          captchaRefresh: translationTexts.captchaRefresh,
        },
        en: {
          inputPrompt: 'captcha',
        },
      },
    };
    const script1 = document.createElement('script');
    script1.src = environment.captchaServiceUrl + '/mtcv1/client/mtcaptcha.min.js';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.src = environment.captchaServiceUrl + '/mtcv1/client/mtcaptcha2.min.js';
    script2.async = true;
    document.head.appendChild(script2);

    // Inject the config into the global window object
    window['mtcaptchaConfig'] = mtcaptchaConfig;
  }

  public getHiddenValue$(mtcaptchaName: string): Observable<string> {
    if (!this.hiddenValuesSubject[mtcaptchaName]) {
      this.hiddenValuesSubject[mtcaptchaName] = new BehaviorSubject<string>('');
    }
    return this.hiddenValuesSubject[mtcaptchaName].asObservable();
  }

  public setHiddenValue(mtcaptchaName:string, value: string): void {
    if (!this.hiddenValuesSubject[mtcaptchaName]) {
      this.hiddenValuesSubject[mtcaptchaName] = new BehaviorSubject<string>('');
    }
    this.hiddenValuesSubject[mtcaptchaName].next(value);
  }

  public callValidateToken(token: any): Observable<any> {
    return this.captchaApi.validateToken(token).pipe(
      first(),
      catchError((error) => throwError(() => error))
    );
  }

  public initializeMTCaptcha(): void {
    this.translate.get('module.public.registration')
      .pipe(
        first(),
        tap((translationTexts) => this.loadMTCaptchaScript(translationTexts))
    ).subscribe();
  }
}

declare global {
  interface Window {
    mtcaptchaConfig: any;
  }
}
